import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  createFilterOptions,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { Colors } from '../../../../../styles/theme';
import { ValidDataService } from '../../../../../service/validDataService';

const filterSuppliers = createFilterOptions();
const filterBrands = createFilterOptions();

const buildSizeSummary = (values = []) => values
  .filter((value) => value !== undefined && value !== null && value !== '' && Number(value) !== 0)
  .join('-');

export default function FrameSectionOrderNew({ frameData, onFrameDataChange, location }) {
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const orderNewFrame = frameData.frameDetails?.orderNewFrame || {};
  const properties = frameData.properties || {};

  const selectedSupplierId = orderNewFrame?.supplierId || '';
  const selectedBrandId = orderNewFrame?.brandId || '';
  const orderStatus = String(orderNewFrame?.orderStatus || orderNewFrame?.status || '').toLowerCase();
  const placeOrderNow = typeof orderNewFrame?.placeOrder === 'boolean'
    ? orderNewFrame.placeOrder
    : orderStatus !== 'queued';

  useEffect(() => {
    let active = true;

    ValidDataService.getAllSuppliers('frames').then((result) => {
      if (!active) {
        return;
      }

      if (result?.code === 0) {
        setSuppliers(result.data || []);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!selectedSupplierId) {
      setBrands([]);
      return () => {
        active = false;
      };
    }

    ValidDataService.getBrandsForSupplier(selectedSupplierId).then((result) => {
      if (!active) {
        return;
      }

      if (result?.code === 0) {
        setBrands(result.data || []);
      }
    });

    return () => {
      active = false;
    };
  }, [selectedSupplierId]);

  const supplierName = useMemo(() => {
    const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(selectedSupplierId));
    return selectedSupplier?.supplierName || orderNewFrame?.supplier?.supplierName || frameData.supplier?.supplierName || '';
  }, [orderNewFrame?.supplier, frameData.supplier, selectedSupplierId, suppliers]);

  const supplierValue = useMemo(() => {
    if (selectedSupplierId) {
      return suppliers.find((supplier) => String(supplier.id) === String(selectedSupplierId)) || null;
    }

    const name = orderNewFrame?.supplier?.supplierName || frameData.supplier?.supplierName || '';
    return name || null;
  }, [selectedSupplierId, suppliers, orderNewFrame?.supplier, frameData.supplier]);

  const brandValue = useMemo(() => {
    if (selectedBrandId) {
      return brands.find((brand) => String(brand.id) === String(selectedBrandId)) || null;
    }

    const name = orderNewFrame?.brand?.name || frameData.brand?.name || '';
    return name || null;
  }, [selectedBrandId, brands, orderNewFrame?.brand, frameData.brand]);

  const setOrderNewField = (fieldPath, value) => {
    onFrameDataChange(`frameDetails.orderNewFrame.${fieldPath}`, value);
  };

  const setMeasurementField = (field, value) => {
    onFrameDataChange(`properties.${field}`, value);
    setOrderNewField(field, value);

    const nextSummary = buildSizeSummary([
      field === 'aMeasurement' ? value : properties.aMeasurement,
      field === 'bMeasurement' ? value : properties.bMeasurement,
      field === 'dblMeasurement' ? value : properties.dblMeasurement,
      field === 'templeLength' ? value : properties.templeLength,
      field === 'edMeasurement' ? value : properties.edMeasurement,
    ]);

    setOrderNewField('sizeCode', nextSummary || null);
    onFrameDataChange('properties.sizeCode', nextSummary || '');
  };

  const derivedSizeCode = buildSizeSummary([
    properties.aMeasurement,
    properties.bMeasurement,
    properties.dblMeasurement,
    properties.templeLength,
    properties.edMeasurement,
  ]) || properties.sizeCode || orderNewFrame?.sizeCode || '';

  const handleSupplierChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object') {
      onFrameDataChange('supplier', { id: newValue.id, supplierName: newValue.supplierName });
      setOrderNewField('supplierId', newValue.id || null);
      setOrderNewField('supplier', { id: newValue.id, supplierName: newValue.supplierName });
    } else if (typeof newValue === 'string') {
      onFrameDataChange('supplier', { supplierName: newValue });
      setOrderNewField('supplierId', null);
      setOrderNewField('supplier', { supplierName: newValue });
    } else {
      onFrameDataChange('supplier', null);
      setOrderNewField('supplierId', null);
      setOrderNewField('supplier', null);
    }

    onFrameDataChange('brand', null);
    setOrderNewField('brandId', null);
    setOrderNewField('brand', null);
  };

  const handleBrandChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object') {
      onFrameDataChange('brand', { id: newValue.id, name: newValue.name });
      setOrderNewField('brandId', newValue.id || null);
      setOrderNewField('brand', { id: newValue.id, name: newValue.name });
    } else if (typeof newValue === 'string') {
      const brand = { id: null, name: newValue };
      onFrameDataChange('brand', brand);
      setOrderNewField('brandId', null);
      setOrderNewField('brand', brand);
    } else {
      onFrameDataChange('brand', null);
      setOrderNewField('brandId', null);
      setOrderNewField('brand', null);
    }
  };

  return (
    <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1, backgroundColor: Colors.secondary }}>
          <Typography sx={{ flexGrow: 1 }}>Order New Frame</Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          {placeOrderNow
            ? 'This frame will place a supplier purchase order immediately and then move through procurement receiving.'
            : 'This frame request will be queued in Procurement > New Orders. Supplier is still required to save the request.'}
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={placeOrderNow}
              onChange={(event) => {
                const checked = event.target.checked;
                setOrderNewField('placeOrder', checked);
                setOrderNewField('orderStatus', checked ? 'submitted' : 'queued');
              }}
            />
          )}
          label="Place Order"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          options={suppliers}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.inputValue || option.supplierName)}
          filterOptions={(options, params) => {
            const filtered = filterSuppliers(options, params);
            const { inputValue } = params;
            const exists = options.some((supplier) => inputValue.toLowerCase() === supplier.supplierName.toLowerCase());
            if (inputValue !== '' && !exists) {
              filtered.push({ inputValue, supplierName: `Add "${inputValue}"` });
            }
            return filtered;
          }}
          value={supplierValue}
          onChange={(event, newValue) => {
            if (newValue?.inputValue) {
              handleSupplierChange(event, { supplierName: newValue.inputValue });
            } else {
              handleSupplierChange(event, newValue);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              const val = newInputValue.trim();
              if (val) {
                setOrderNewField('supplier', { supplierName: val });
                setOrderNewField('supplierId', null);
                onFrameDataChange('supplier', { supplierName: val });
              } else {
                setOrderNewField('supplier', null);
                setOrderNewField('supplierId', null);
                onFrameDataChange('supplier', null);
              }
            }
          }}
          renderOption={(props, option) => <li {...props}>{option.supplierName}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supplier *"
              size="small"
              fullWidth
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          options={brands}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.inputValue || option.name)}
          filterOptions={(options, params) => {
            const filtered = filterBrands(options, params);
            const { inputValue } = params;
            const exists = options.some((brand) => inputValue.toLowerCase() === brand.name.toLowerCase());
            if (inputValue !== '' && !exists) {
              filtered.push({ inputValue, name: `Add "${inputValue}"` });
            }
            return filtered;
          }}
          value={brandValue}
          onChange={(event, newValue) => {
            if (newValue?.inputValue) {
              handleBrandChange(event, { name: newValue.inputValue });
            } else {
              handleBrandChange(event, newValue);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              const val = newInputValue.trim();
              const brand = val ? { id: null, name: val } : null;
              setOrderNewField('brand', brand);
              setOrderNewField('brandId', null);
              onFrameDataChange('brand', brand);
            }
          }}
          renderOption={(props, option) => <li {...props}>{option.name}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Brand"
              size="small"
              fullWidth
              helperText={!selectedSupplierId ? 'Select a supplier first to link to an existing brand.' : undefined}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Model #"
          size="small"
          value={frameData.modelNumber || ''}
          onChange={(event) => {
            onFrameDataChange('modelNumber', event.target.value);
            setOrderNewField('modelNumber', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Location"
          size="small"
          value={location?.name || frameData.location?.name || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="A"
          size="small"
          type="number"
          value={properties.aMeasurement || ''}
          onChange={(event) => setMeasurementField('aMeasurement', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="B"
          size="small"
          type="number"
          value={properties.bMeasurement || ''}
          onChange={(event) => setMeasurementField('bMeasurement', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="DBL"
          size="small"
          type="number"
          value={properties.dblMeasurement || ''}
          onChange={(event) => setMeasurementField('dblMeasurement', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="ED"
          size="small"
          type="number"
          value={properties.edMeasurement || ''}
          onChange={(event) => setMeasurementField('edMeasurement', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Eye"
          size="small"
          type="number"
          value={properties.eyeSize || ''}
          onChange={(event) => setMeasurementField('eyeSize', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Temp"
          size="small"
          type="number"
          value={properties.templeLength || ''}
          onChange={(event) => setMeasurementField('templeLength', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Size Summary"
          size="small"
          value={derivedSizeCode}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Retail Price"
          size="small"
          type="number"
          value={frameData.listPrice || ''}
          onChange={(event) => {
            onFrameDataChange('listPrice', event.target.value);
            setOrderNewField('listPrice', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Color"
          size="small"
          value={properties.frameColor || orderNewFrame?.frameColor || ''}
          onChange={(event) => {
            onFrameDataChange('properties.frameColor', event.target.value);
            setOrderNewField('frameColor', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label="Type"
          size="small"
          value={properties.frameType || orderNewFrame?.frameType || ''}
          onChange={(event) => {
            onFrameDataChange('properties.frameType', event.target.value);
            setOrderNewField('frameType', event.target.value);
          }}
        >
          <MenuItem value="">Select type</MenuItem>
          <MenuItem value="Optical">Optical</MenuItem>
          <MenuItem value="Sunglasses">Sunglasses</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Expected Date"
          size="small"
          type="date"
          value={orderNewFrame?.expectedDate || ''}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => setOrderNewField('expectedDate', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Order Quantity"
          size="small"
          type="number"
          value={orderNewFrame?.quantity || 1}
          onChange={(event) => setOrderNewField('quantity', event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Procurement Notes"
          size="small"
          multiline
          minRows={3}
          value={orderNewFrame?.notes || ''}
          onChange={(event) => setOrderNewField('notes', event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Supplier Summary"
          size="small"
          value={supplierName}
          disabled
        />
      </Grid>
    </Grid>
  );
}
